// THE APP'S `.alert(…)` ON THE WEB (WEB-PARITY-1C, DT-25): a title, a message, and the app's own buttons, over the page.
//
// A modal `alertdialog`: focus moves to its first button when it opens and returns to where it was when it closes, and
// Escape is the cancel button where the app gives it one (`role: .cancel`) — never where it does not, because an alert
// with no cancel in the app (the square footage, the purchase) is one the owner answers, not dismisses.

import { useEffect, useId, useRef, type ReactNode } from "react";

export interface AlertButton {
  readonly label: string;
  /** The words' id on the element (`data-held-copy`), as every sentence this client holds carries it. */
  readonly wordsId: string;
  readonly onPress: () => void;
  /** The app's `role: .cancel`: Escape presses it. */
  readonly cancel?: boolean;
  /** The app's `role: .destructive`: drawn in the danger colour, never as the amber primary (WALK-FIX-1). */
  readonly destructive?: boolean;
}

export function AppAlert({
  title,
  titleId,
  kind,
  children,
  buttons,
}: {
  title: string;
  titleId: string;
  /** Which alert this is, on the element for us. */
  kind: string;
  children: ReactNode;
  buttons: readonly AlertButton[];
}) {
  const headingId = useId();
  const first = useRef<HTMLButtonElement>(null);
  const cancel = buttons.find((b) => b.cancel === true);

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    first.current?.focus();
    return () => {
      before?.focus?.();
    };
  }, []);

  return (
    <div className="alert-backdrop">
      <div
        className="alert"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-alert={kind}
        onKeyDown={(e) => {
          if (e.key === "Escape" && cancel !== undefined) {
            e.preventDefault();
            cancel.onPress();
          }
        }}
      >
        <h2 id={headingId} data-held-copy={titleId}>
          {title}
        </h2>
        <div className="alert-message">{children}</div>
        <div className="alert-buttons">
          {buttons.map((b, i) => (
            <button
              key={b.wordsId}
              ref={i === 0 ? first : undefined}
              type="button"
              className={b.cancel === true ? "btn ghost" : b.destructive === true ? "btn danger" : "btn primary"}
              data-held-copy={b.wordsId}
              onClick={b.onPress}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
